import axios from 'axios';

// Empty-string fallback ensures relative URLs (/api/...) in production builds
// where REACT_APP_BACKEND_URL is not set at compile time.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

// ---- Auto-retry interceptor on 5xx / network errors ----
// Retries up to MAX_RETRIES times with exponential backoff.
// Only retries idempotent-safe methods (GET, HEAD, OPTIONS).
const MAX_RETRIES = 2;
const RETRY_METHODS = new Set(['get', 'head', 'options']);

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// Use a WeakMap-keyed-by-config-object so the counter survives Axios mergeConfig.
// We key by a stable string ID injected into the config before the request is sent.
const _retryCounts = new Map();

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('okv_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // Stamp a unique retry-tracking ID if not already present.
    if (!config.__retryId) config.__retryId = Math.random().toString(36).slice(2);
    return config;
});

api.interceptors.response.use(
    (resp) => {
        // Clean up tracking entry on success.
        if (resp.config?.__retryId) _retryCounts.delete(resp.config.__retryId);
        return resp;
    },
    async (error) => {
        const config = error.config || {};
        const status = error.response?.status;
        const method = (config.method || 'get').toLowerCase();
        const isNetworkErr = !error.response;
        const isServerErr = status && status >= 500 && status < 600;
        // Only retry on 5xx or network failures for safe methods
        if (!isServerErr && !isNetworkErr) return Promise.reject(error);
        if (!RETRY_METHODS.has(method)) return Promise.reject(error);

        const id = config.__retryId;
        if (!id) return Promise.reject(error);

        const count = (_retryCounts.get(id) || 0) + 1;
        if (count > MAX_RETRIES) {
            _retryCounts.delete(id);
            // Notify a friendly toast on last failure (only once per call chain)
            try {
                const { toast } = await import('sonner');
                toast?.error('Servicio temporalmente no disponible. Reintenta en unos segundos.', { id: 'api-5xx' });
            } catch { /* noop */ }
            return Promise.reject(error);
        }
        _retryCounts.set(id, count);
        // Exponential backoff: 600ms, 900ms
        const delay = 300 + Math.pow(2, count) * 300;
        await sleep(delay);
        return api.request(config);
    }
);

export const setToken = (t) => {
    if (t) localStorage.setItem('okv_token', t);
    else localStorage.removeItem('okv_token');
};

export const getToken = () => localStorage.getItem('okv_token');

export const setStoredRole = (r) => {
    if (r) localStorage.setItem('okv_role', r);
    else localStorage.removeItem('okv_role');
};
export const getStoredRole = () => localStorage.getItem('okv_role');

export const mediaUrl = (u) => {
    if (!u) return '';
    if (u.startsWith('http')) return u;
    return `${BACKEND_URL}${u}`;
};

export const formatMoney = (v, currency = 'MXN') => {
    try {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);
    } catch {
        return `$${v}`;
    }
};
